import { Button, Divider, IconButton, Typography } from 'omakase';
import { Container, Grid } from 'ui';
import { IconClose } from 'ui/icons';
import { IconCrossLarge } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { consumer } from '@tock/coretypes';
import { getPlaylistType } from 'utils/profileUtils';
import { playlistsByIdSelector } from 'selectors/patronSelectors';

import {
  businessNameRemoved,
  gridList,
  gridListProfile,
  justRemovedBox,
  mobileDivider,
  readdButton,
  titleBox,
} from 'routes/protected/account/profile/profilePlaylistDetailsRouteCssDefs';
import { useDidUpdateEffect, useMountEffect, usePrevious } from '@tock/hooks';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'react-redux';
import { useSharePlaylist } from 'hooks/useLoadPlaylists';
import Anchor from 'components/Anchor';
import EditPlaylistDialog from 'components/profile/playlists/EditPlaylistDialog';
import PlaylistBusiness from 'components/profile/playlists/PlaylistBusiness';
import PlaylistOptionsDialog from 'components/profile/playlists/PlaylistOptionsDialog';
import PlaylistTypeIcon from 'components/profile/playlists/PlaylistTypeIcon';
import ProfilePlaylistDetailsTitle, {
  PlaylistPermissionsMode,
} from 'components/profile/playlists/ProfilePlaylistDetailsTitle';
import React, { useState } from 'react';
import _ from 'lodash';

interface MatchParams {
  playlistId: string;
}

interface Props {
  onDeletePlaylistDone?(): void;
}

const ProfilePlaylistDetailsRoute: React.FunctionComponent<Props> = ({ onDeletePlaylistDone }) => {
  const navigation = useSelector((state: StoreType) => state.navigation);
  const match = useRouteMatch<MatchParams>();
  const playlistsById = useSelector(playlistsByIdSelector);
  const playlistId = _.toNumber(match.params.playlistId);
  const { actions } = useActions();

  const playlist = playlistsById[playlistId];

  useMountEffect(() => {
    actions.navigation.set({ title: 'My list', hasBack: true });
    return () => {
      actions.navigation.set(navigation);
    };
  });

  // todo loading state
  if (!playlist) {
    return null;
  }

  return <PlaylistDetails onDeletePlaylist={onDeletePlaylistDone} playlist={playlist} />;
};

export default ProfilePlaylistDetailsRoute;

interface PlaylistDetailsProps {
  playlist: consumer.Playlist;
  publicPage?: boolean;
  onDeletePlaylist?(): void;
}

export const PlaylistDetails: React.FunctionComponent<PlaylistDetailsProps> = ({
  playlist,
  publicPage,
  onDeletePlaylist,
}) => {
  const { actions } = useActions();
  const [originalPlaylistItems, setOriginalPlaylistItems] = useState(playlist.items);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const prevPlaylistId = usePrevious(playlist.id);

  // tab changing doesn't cause a remount, just a prop change
  useDidUpdateEffect(() => {
    if (playlist.id !== prevPlaylistId) {
      setOriginalPlaylistItems(playlist.items);
    }
  });

  const handleSharePlaylist = useSharePlaylist();

  const handleRemovePlaylistItem = (playlistItemId: number) => {
    actions.patron.deletePlaylistItem(playlist.id, playlistItemId);
  };

  const handleReaddPlaylistItem = (businessId: number, note?: string) => {
    actions.patron.upsertPlaylistItem(playlist.id, { businessId, note });
  };

  const handleCloseJustRemoved = (itemId: number) => {
    setOriginalPlaylistItems(originalPlaylistItems.filter((item) => item.id !== itemId));
  };

  const handleChangePermissions = (mode: PlaylistPermissionsMode) => {
    actions.patron.upsertPlaylist({ ...playlist, isPublic: mode === 'PUBLIC' });
  };

  const handleChangeNote = _.debounce((addRequest: consumer.IAddPlaylistItemRequest) => {
    actions.patron.upsertPlaylistItem(playlist.id, addRequest);
  }, 200);

  const currentPlaylistItemsByBusinessId: Dictionary<consumer.IPlaylistItem> = _.keyBy(
    playlist.items,
    (item) => item.business?.id || 0
  );

  const playlistType = getPlaylistType(playlist);

  const handleCopyLink = () => {
    handleSharePlaylist(playlist);
  };

  return (
    <>
      <div css={(theme) => titleBox(theme, publicPage)}>
        <Container maxWidth="md">
          <ProfilePlaylistDetailsTitle
            onEdit={() => setIsEditOpen(true)}
            playlist={playlist}
            publicPage={publicPage}
            onCopyLink={handleCopyLink}
            onChangePermissions={handleChangePermissions}
          />
        </Container>
      </div>

      <div css={publicPage ? gridList : gridListProfile}>
        <Container disableGutters={!publicPage} maxWidth={!publicPage ? false : 'md'}>
          <Grid container spacing={2}>
            {originalPlaylistItems.map((item) => (
              <Grid item xs={12} key={`playlistItem-${item.id}`}>
                {item.business?.id && currentPlaylistItemsByBusinessId[item.business.id] ? (
                  <PlaylistBusiness
                    isPublicPage={publicPage}
                    playlistItem={currentPlaylistItemsByBusinessId[item.business.id]}
                    playlistType={playlistType}
                    onRemovePlaylistItem={handleRemovePlaylistItem}
                    onChangeNote={handleChangeNote}
                  />
                ) : (
                  <Grid container justify="space-between" css={justRemovedBox}>
                    <Grid item>
                      <Typography component="h5" variant="body02Strong">
                        <Anchor href={`/${item.business?.domainName}`} css={businessNameRemoved}>
                          {item.business?.name}
                        </Anchor>{' '}
                        has been removed from this list
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        size="small"
                        css={readdButton}
                        variant="ghost"
                        iconStart={<PlaylistTypeIcon playlistType={playlistType} />}
                        onClick={() =>
                          item.business?.id &&
                          handleReaddPlaylistItem(item.business.id, item.note || undefined)
                        }
                      >
                        Resave
                      </Button>
                      <IconButton
                        variant="ghost"
                        size="small"
                        onClick={() => item.id && handleCloseJustRemoved(item.id)}
                        aria-label="Close"
                      >
                        <IconMigrationUtil
                          legacyIcon={<IconClose />}
                          newIcon={<IconCrossLarge />}
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                )}

                <Divider css={mobileDivider} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </div>
      <PlaylistOptionsDialog
        onEditListDetails={() => {
          setIsEditOpen(false);
          setIsDetailsOpen(true);
        }}
        onDeleteListDone={onDeletePlaylist}
        playlist={playlist}
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onShareList={handleCopyLink}
      />
      <EditPlaylistDialog
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        playlist={playlist}
      />
    </>
  );
};
